import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  X,
  Building2,
  ShoppingCart,
  Calendar,
  Package,
  Loader2,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clientsService } from '@/services/clients.service';
import { api } from '@/lib/axios';
import { cn } from '@caruso/ui';

interface ClientQuickInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

interface ClientQuickInfo {
  client: {
    id: string;
    name: string;
    phone: string;
    email?: string;
  };
  company?: {
    id: string;
    name: string;
  };
  stats: {
    totalOrders: number;
    averageTicket: number;
    lastOrderDate?: string;
    lastCheckinDate?: string;
    topProducts: Array<{
      name: string;
      quantity: number;
    }>;
  };
}

// Buscar info rapida do cliente
async function getClientQuickInfo(clientId: string): Promise<ClientQuickInfo> {
  const response = await api.get(`/webchat/clients/${clientId}/quick-info`);
  return response.data;
}

export function ClientQuickInfoDialog({
  isOpen,
  onClose,
  clientId,
}: ClientQuickInfoDialogProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['client-quick-info', clientId],
    queryFn: () => getClientQuickInfo(clientId),
    enabled: isOpen && !!clientId,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return format(new Date(dateStr), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-brand-ink border border-brand-gold/20 rounded-xl w-full max-w-md shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-brand-gold/10">
                <h2 className="text-lg font-serif text-brand-gold">Info Rapida</h2>
                <button
                  onClick={onClose}
                  className="p-1 rounded-lg hover:bg-brand-gold/10 text-brand-ivory/50 hover:text-brand-gold transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-brand-gold animate-spin" />
                  </div>
                ) : error ? (
                  <div className="text-center py-8 text-brand-ivory/50">
                    <p>Erro ao carregar informacoes</p>
                  </div>
                ) : data ? (
                  <div className="space-y-4">
                    {/* Empresa */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-brand-smoke/30">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-brand-ivory/50">Empresa</p>
                        <p className="text-brand-ivory font-medium truncate">
                          {data.company?.name || 'Sem empresa vinculada'}
                        </p>
                      </div>
                    </div>

                    {/* Ticket Medio */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-brand-smoke/30">
                      <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-brand-ivory/50">Ticket Medio</p>
                        <p className="text-brand-ivory font-medium">
                          {data.stats.totalOrders > 0
                            ? formatCurrency(data.stats.averageTicket)
                            : 'Sem pedidos'}
                        </p>
                        <p className="text-xs text-brand-ivory/40">
                          {data.stats.totalOrders} pedido{data.stats.totalOrders !== 1 ? 's' : ''} no total
                        </p>
                      </div>
                    </div>

                    {/* Ultima Compra */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-brand-smoke/30">
                      <div className="w-10 h-10 rounded-lg bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
                        <ShoppingCart className="w-5 h-5 text-brand-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-brand-ivory/50">Ultima Compra</p>
                        <p className="text-brand-ivory font-medium">
                          {data.stats.lastOrderDate
                            ? formatDate(data.stats.lastOrderDate)
                            : 'Nenhuma compra'}
                        </p>
                      </div>
                    </div>

                    {/* Ultimo Checkin */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-brand-smoke/30">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-brand-ivory/50">Ultimo Check-in</p>
                        <p className="text-brand-ivory font-medium">
                          {data.stats.lastCheckinDate
                            ? formatDate(data.stats.lastCheckinDate)
                            : 'Nenhum check-in'}
                        </p>
                      </div>
                    </div>

                    {/* Top 3 Produtos */}
                    <div className="p-3 rounded-lg bg-brand-smoke/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="w-4 h-4 text-brand-gold" />
                        <p className="text-xs text-brand-ivory/50">Top 3 Produtos</p>
                      </div>
                      {data.stats.topProducts.length > 0 ? (
                        <div className="space-y-2">
                          {data.stats.topProducts.map((product, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between text-sm"
                            >
                              <span className="text-brand-ivory truncate flex-1">
                                {index + 1}. {product.name}
                              </span>
                              <span className="text-brand-ivory/50 ml-2">
                                {product.quantity}x
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-brand-ivory/40">
                          Nenhum produto comprado
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
